import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = '/api';

export const useClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showImportClientsModal, setShowImportClientsModal] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [newClient, setNewClient] = useState({ name: '', phone: '', type: 'cliente' });
  const [currentClientPage, setCurrentClientPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  
  const { csrfToken, refreshCsrfToken } = useAuth();

  const ensureFreshCsrfToken = async () => {
    try {
      const result = await refreshCsrfToken();
      if (!result.success) {
        console.error('Error al refrescar el token CSRF');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error al refrescar el token CSRF:', error);
      return false;
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar los clientes. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClient = async () => {
    try {
      setLoading(true);
      
      if (!await ensureFreshCsrfToken()) {
        setError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      const clientData = {
        name: newClient.name,
        phone: newClient.phone,
        type: newClient.type || 'cliente'
      };

      if (newClient.id) {
        await axios.put(`${API_URL}/clients/${newClient.id}`, clientData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      } else {
        await axios.post(`${API_URL}/clients`, clientData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      }

      await fetchClients();
      setNewClient({ name: '', phone: '', type: 'cliente' });
      setShowClientForm(false);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al guardar el cliente. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este cliente?')) {
      try {
        setLoading(true);
        
        if (!await ensureFreshCsrfToken()) {
          setError('Error al obtener un token CSRF válido. Intente recargar la página.');
          setLoading(false);
          return;
        }
        
        await axios.delete(`${API_URL}/clients/${clientId}`, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });

        await fetchClients();
        setError(null);
      } catch (err) {
        console.error('Error:', err);
        setError('Error al eliminar el cliente. Por favor, intente de nuevo.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditClient = (client) => {
    setNewClient({
      id: client.id,
      name: client.name,
      phone: client.phone,
      type: client.type || 'cliente'
    });
    setShowClientForm(true);
  };

  const handleClientFileUpload = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target.result);
          
          let clientsToImport = [];
          
          if (data.data && Array.isArray(data.data)) {
            clientsToImport = data.data;
          } else if (Array.isArray(data)) {
            clientsToImport = data;
          } else {
            throw new Error('Formato de datos no reconocido. Se espera un array de clientes.');
          }
          
          const validClients = clientsToImport.filter(client => 
            client && client.name && (client.cellphone || client.phone || client.cellphone_formatted)
          );
          
          if (validClients.length === 0) {
            throw new Error('No se encontraron clientes válidos para importar. Cada cliente debe tener al menos nombre y teléfono.');
          }
          
          setImportData(validClients);
          setImportPreview(validClients.slice(0, 5));
          setImportError('');
        } catch (error) {
          console.error('Error al procesar archivo JSON:', error);
          setImportError(`Error al procesar el archivo: ${error.message}`);
          setImportData(null);
          setImportPreview([]);
        }
      };
      
      reader.readAsText(file);
    }
  };

  // useClients.js - Versión corregida de handleImportClients
  const handleImportClients = async () => {
    if (!importData) return;
    
    try {
      setLoading(true);
      setImportError('');
      setImportSuccess('');
      
      if (!await ensureFreshCsrfToken()) {
        setImportError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      const importedClients = [];
      const errorClients = [];
      const skippedClients = [];
      
      // Primero obtener todos los clientes existentes para verificar duplicados
      const existingClientsResponse = await axios.get(`${API_URL}/clients`);
      const existingClients = existingClientsResponse.data;
      
      for (const client of importData) {
        try {
          const clientPhone = client.cellphone || client.phone || client.cellphone_formatted || '';
          
          // Verificar si el cliente ya existe por nombre y teléfono
          const existingClient = existingClients.find(existing => 
            existing.name.toLowerCase() === client.name.toLowerCase() && 
            existing.phone === clientPhone
          );
          
          if (existingClient) {
            skippedClients.push(client.name);
            continue; // Saltar este cliente si ya existe
          }
          
          const clientData = {
            name: client.name,
            phone: clientPhone,
            type: client.type || 'cliente',
            _csrf: csrfToken
          };
          
          const response = await axios.post(`${API_URL}/clients`, clientData, {
            headers: {
              'X-CSRF-Token': csrfToken
            }
          });
          
          importedClients.push(response.data);
          existingClients.push(response.data); // Agregar al array para siguientes verificaciones
        } catch (err) {
          console.error('Error al importar cliente:', client.name, err);
          errorClients.push({ 
            client: client.name, 
            error: err.response?.data?.error || 'Error desconocido' 
          });
        }
      }
      
      // Mejorar el mensaje de éxito para incluir información sobre clientes omitidos
      if (errorClients.length === 0 && skippedClients.length === 0) {
        setImportSuccess(`Se importaron ${importedClients.length} clientes con éxito.`);
      } else {
        let message = `Se importaron ${importedClients.length} clientes.`;
        if (skippedClients.length > 0) {
          message += ` Se omitieron ${skippedClients.length} clientes duplicados.`;
        }
        if (errorClients.length > 0) {
          message += ` Hubo errores en ${errorClients.length} clientes.`;
        }
        setImportSuccess(message);
      }
      
      await fetchClients();
      
      setTimeout(() => {
        setShowImportClientsModal(false);
        setImportData(null);
        setImportPreview([]);
        setImportError('');
        setImportSuccess('');
      }, 3000);
      
    } catch (err) {
      console.error('Error general en la importación:', err);
      setImportError('Error en el proceso de importación. Intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const getPaginatedClients = () => {
    let filtered = clients;
    
    if (clientSearchTerm) {
      filtered = filtered.filter(client => {
        const name = client.name.toLowerCase();
        const phone = client.phone?.toLowerCase() || '';
        const term = clientSearchTerm.toLowerCase();
        return name.includes(term) || phone.includes(term);
      });
    }
    
    const startIndex = (currentClientPage - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  };

  const handleNextPage = () => {
    if (currentClientPage * itemsPerPage < clients.length) {
      setCurrentClientPage(currentClientPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentClientPage > 1) {
      setCurrentClientPage(currentClientPage - 1);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentClientPage(1);
  };

  return {
    clients,
    loading,
    error,
    setError,
    showClientForm,
    setShowClientForm,
    showImportClientsModal,
    setShowImportClientsModal,
    importData,
    setImportData,          
    importPreview,
    setImportPreview,       
    importError,
    setImportError,         
    importSuccess,
    setImportSuccess,       
    newClient,
    setNewClient,
    currentClientPage,
    itemsPerPage,
    clientSearchTerm,
    setClientSearchTerm,
    fetchClients,
    handleSaveClient,
    handleDeleteClient,
    handleEditClient,
    handleClientFileUpload,
    handleImportClients,
    getPaginatedClients,
    handleNextPage,
    handlePrevPage,
    handleItemsPerPageChange
  };
};
